// Use around a page so a render crash shows a way out instead of a blank
// screen. routes/pages.tsx puts one around every lazy page (RouteBoundary,
// resetKey = the path), so navigating away clears the error.
//
// A chunk that fails to load (a new build replaced the old files) is not a
// crash: it says a newer version is ready, with only "Reload page".

import { Component, type ErrorInfo, type ReactNode } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { Link } from "react-router-dom";

import { Button, buttonClasses } from "./Button";
import { EmptyState } from "./EmptyState";
import { ErrorDetails } from "./LoadError";

interface ErrorBoundaryProps {
  /** A change clears a caught error (the route's path); nothing else remounts. */
  resetKey: string;
  children: ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

const CHUNK_LOAD = /dynamically imported module|Importing a module script failed|Loading chunk/i;

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: unknown): ErrorBoundaryState {
    return { error: error instanceof Error ? error : new Error(String(error)) };
  }

  componentDidCatch(error: unknown, info: ErrorInfo) {
    console.error("A page crashed:", error, info.componentStack);
  }

  componentDidUpdate(prev: ErrorBoundaryProps) {
    if (prev.resetKey !== this.props.resetKey && this.state.error) this.setState({ error: null });
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    return (
      <div className="flex h-full w-full items-center justify-center">
        {CHUNK_LOAD.test(error.message) ? <NewerVersion /> : <Crashed message={error.message} />}
      </div>
    );
  }
}

function reload() {
  window.location.reload();
}

function NewerVersion() {
  return (
    <EmptyState
      icon={<RefreshCw strokeWidth={1.75} />}
      title="A newer version is ready"
      description="Reload to get it."
      action={<Button onClick={reload}>Reload page</Button>}
    />
  );
}

function Crashed({ message }: { message: string }) {
  const home = window.location.pathname.startsWith("/companion") ? "/companion/today" : "/dashboard";
  return (
    <EmptyState
      tone="error"
      icon={<AlertTriangle strokeWidth={1.75} />}
      title="This page hit a problem"
      description={
        <>
          Something on this page broke. Reload it to carry on; nothing you saved is lost.
          <ErrorDetails detail={message} />
        </>
      }
      action={<Button onClick={reload}>Reload page</Button>}
      secondaryAction={
        <Link to={home} className={buttonClasses({ variant: "secondary" })}>
          Go to Home
        </Link>
      }
    />
  );
}

/**
 * Around the signed-in app at the root (App.tsx): a crash in a shell, the
 * sidebar or the recording bar (outside every page's own boundary) replaces
 * only the app, never the root's siblings: the meeting recorder's lifecycle
 * (its close guard, the Mac app's Stop and flush) stays mounted, and a
 * recording carries on until someone stops it. No router here: a plain way out.
 */
export class RootBoundary extends Component<{ children: ReactNode }, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: unknown): ErrorBoundaryState {
    return { error: error instanceof Error ? error : new Error(String(error)) };
  }

  componentDidCatch(error: unknown, info: ErrorInfo) {
    console.error("Corvus crashed:", error, info.componentStack);
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    return (
      <div className="flex app-vh w-full items-center justify-center px-4 pt-safe pb-safe">
        <EmptyState
          tone="error"
          icon={<AlertTriangle strokeWidth={1.75} />}
          title="Corvus hit a problem"
          description={
            <>
              Something broke. Reload the page to carry on; nothing you saved is lost. A meeting being recorded carries on: stop it
              from the bar at the top first.
              <ErrorDetails detail={error.message} />
            </>
          }
          action={<Button onClick={reload}>Reload page</Button>}
        />
      </div>
    );
  }
}
